import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, type ReactNode } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "./components/NavbarComponent";
import Home from "./components/Home";
import PatientDetails from "./components/PatientDetails";
import AddPatient from "./components/AddPatient";
import Login from "./components/Login";
import Register from "./components/Register";
import ErrorBoundary from "./components/ErrorBoundary";
import { DEFAULTS, AUTH_EVENTS, MESSAGES } from "./constants/app";
import { auth } from "./utils";

export default function App() {
  // Initialize state from localStorage using utility functions
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const loggedIn = auth.isLoggedIn();
    return loggedIn;
  });
  const [userEmail, setUserEmail] = useState(() => auth.getUserEmail());
  const [isAdmin, setIsAdmin] = useState(() => auth.isAdmin());
  const [hospitalName, setHospitalName] = useState(() =>
    auth.getHospitalName()
  );
  const [userId, setUserId] = useState(() => auth.getUserId());

  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = (
    email: string,
    role: 'admin' | 'super_admin',
    hospitalNameParam?: string,
    userIdParam?: string,
    token?: string
  ) => {
    const displayName = hospitalNameParam || DEFAULTS.HOSPITAL_NAME;
    const userIdValue = userIdParam || "";
    const isAdminUser = role === 'super_admin'; // Calculate from role

    // Update state
    setIsLoggedIn(true);
    setUserEmail(email);
    setIsAdmin(isAdminUser);
    setHospitalName(displayName);
    setUserId(userIdValue);

    // Save to localStorage using utility (now accepts token)
    auth.saveUserSession(email, role, displayName, userIdValue, token);
    
    // Add a delay to ensure cookie is processed by browser before navigation
    // This is especially important for cross-origin cookies in incognito mode
    // Increased delay to give browser more time to process the cookie
    setTimeout(() => {
      navigate("/");
    }, 300);
  };

  const resetAuthState = () => {
    setIsLoggedIn(false);
    setUserEmail("");
    setIsAdmin(false);
    setHospitalName("");
    setUserId("");
  };

  const handleLogout = () => {
    resetAuthState();

    // Clear localStorage using utility
    auth.clearUserSession();
    navigate("/");
  };

  // Handle global session expiry: when an API call detects an expired/invalid
  // token it clears the session and dispatches this event. Reset auth state,
  // redirect to Login, and inform the user. Guarded on isLoggedIn so it can't
  // loop or double-toast once we're already on the Login screen.
  useEffect(() => {
    const handleSessionExpired = () => {
      if (!isLoggedIn) return;
      resetAuthState();
      auth.clearUserSession();
      navigate("/");
      toast.info(MESSAGES.AUTH.SESSION_EXPIRED);
    };

    window.addEventListener(AUTH_EVENTS.SESSION_EXPIRED, handleSessionExpired);
    return () => {
      window.removeEventListener(
        AUTH_EVENTS.SESSION_EXPIRED,
        handleSessionExpired
      );
    };
  }, [isLoggedIn, navigate]);

  // Prevent browser back navigation when logged out (HashRouter: avoid stripping the hash while logged in)
  useEffect(() => {
    if (isLoggedIn) return;
    const hashSafe = `${window.location.pathname}${window.location.search}#/`;
    window.history.pushState(null, "", hashSafe);
  }, [isLoggedIn]);

  const renderAuthenticatedPage = (children: ReactNode) => (
    <div className="min-h-screen bg-guardian-10 flex flex-col">
      <Navbar
        isLoggedIn={true}
        userName={hospitalName}
        onLogout={handleLogout}
      />
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-guardian-10">
        <Routes>
          <Route path="/register" element={<Register />} />
          <Route
            path="/"
            element={
              !isLoggedIn ? (
                <Login onLogin={handleLogin} />
              ) : (
                renderAuthenticatedPage(
                  <Home
                    userEmail={userEmail}
                    isAdmin={isAdmin}
                    userId={userId}
                  />
                )
              )
            }
          />
          <Route
            path="/add-patient"
            element={
              !isLoggedIn ? (
                <Login onLogin={handleLogin} />
              ) : (
                renderAuthenticatedPage(
                  <AddPatient />
                )
              )
            }
          />
          <Route
            path="/patient/:id"
            element={
              !isLoggedIn ? (
                <Login onLogin={handleLogin} />
              ) : (
                renderAuthenticatedPage(
                  <PatientDetails />
                )
              )
            }
          />
          <Route
            path="*"
            element={<div>Route not found: {location.pathname}</div>}
          />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </ErrorBoundary>
  );
}
